import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchPublicLink, submitPublicNps, type PublicLinkInfo } from '../api/nps';
import { ApiError } from '../api/client';

type Phase = 'loading' | 'form' | 'done' | 'gone';

export default function NpsRespond() {
  const { token = '' } = useParams();
  const [phase, setPhase] = useState<Phase>('loading');
  const [info, setInfo] = useState<PublicLinkInfo | null>(null);
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [goneMsg, setGoneMsg] = useState('This link is no longer active.');

  useEffect(() => {
    let alive = true;
    fetchPublicLink(token)
      .then((data) => {
        if (!alive) return;
        setInfo(data);
        setPhase(data.alreadySubmitted ? 'done' : 'form');
      })
      .catch((err) => {
        if (!alive) return;
        setGoneMsg(err instanceof ApiError ? err.message : 'This link is no longer active.');
        setPhase('gone');
      });
    return () => {
      alive = false;
    };
  }, [token]);

  async function handleSubmit() {
    if (score == null) {
      setError('Please choose a score from 0 to 10.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await submitPublicNps(token, score, comment);
      setPhase('done');
    } catch (err) {
      // 409 = already responded from this device → still show the thank-you.
      if (err instanceof ApiError && err.status === 409) {
        setPhase('done');
      } else {
        setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="nps-respond-shell">
      <div className="nps-respond-card">
        <div className="nps-respond-brand">
          <div className="logo-mark">A</div>
          <span>Amada Senior Care</span>
        </div>

        {phase === 'loading' && <p className="nps-respond-muted">Loading…</p>}

        {phase === 'gone' && (
          <>
            <h1>Survey unavailable</h1>
            <p className="nps-respond-muted">{goneMsg}</p>
          </>
        )}

        {phase === 'done' && (
          <div className="nps-respond-thanks">
            <div className="nps-respond-check" aria-hidden="true">✓</div>
            <h1>Thank you!</h1>
            <p className="nps-respond-muted">
              Your feedback has been recorded{info ? ` for ${info.locationName}` : ''}. We
              appreciate you taking the time.
            </p>
          </div>
        )}

        {phase === 'form' && info && (
          <>
            <p className="nps-respond-location">{info.locationName}</p>
            <h1 className="nps-respond-prompt">{info.question.prompt}</h1>

            <div className="nps-scale" role="radiogroup" aria-label="Score from 0 to 10">
              {Array.from({ length: 11 }, (_, n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={score === n}
                  className={`nps-scale-btn${score === n ? ' selected' : ''}`}
                  onClick={() => setScore(n)}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="nps-scale-labels">
              <span>{info.question.lowLabel}</span>
              <span>{info.question.highLabel}</span>
            </div>

            <label className="nps-comment-label" htmlFor="nps-comment">
              {info.question.commentPrompt}
            </label>
            <textarea
              id="nps-comment"
              className="nps-comment"
              rows={3}
              maxLength={2000}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Your feedback…"
            />
            <p className="nps-respond-privacy">
              Your response is anonymous. Please don’t include names or personal details in your
              comment.
            </p>

            {error && <div className="nps-respond-error">{error}</div>}

            <button
              type="button"
              className="nps-submit-btn"
              disabled={busy || score == null}
              onClick={handleSubmit}
            >
              {busy ? 'Submitting…' : 'Submit'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
